<template>
    <div class="px-3 py-3 sm:px-5 sm:py-5">
        <!-- Foundation Header Banner -->
        <div class="mb-6 bg-gradient-to-r from-pink-500 to-purple-600 rounded-lg shadow-lg overflow-hidden">
            <div class="px-6 py-8 text-white">
                <h1 class="text-2xl sm:text-3xl font-bold mb-2">{{ foundation.name }}</h1>
                <p class="text-pink-100 mb-4">Campaign Impact Leaderboard</p>

                <div class="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div>
                        <div class="text-xs text-pink-100">Foundation Score</div>
                        <div class="text-2xl font-bold">{{ foundation.impact_score ? foundation.impact_score.toFixed(1) : 'N/A' }}</div>
                    </div>
                    <div>
                        <div class="text-xs text-pink-100">Total Campaigns</div>
                        <div class="text-2xl font-bold">{{ campaigns.length }}</div>
                    </div>
                    <div>
                        <div class="text-xs text-pink-100">Total Donors</div>
                        <div class="text-2xl font-bold">{{ formatNumber(foundation.impact_donor_count || 0) }}</div>
                    </div>
                    <div>
                        <div class="text-xs text-pink-100">Total Raised</div>
                        <div class="text-2xl font-bold">${{ formatCurrency(foundation.total_raised_with_children_campaigns || 0) }}</div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Advanced Filter Controls -->
        <div class="bg-gradient-to-r from-pink-50 to-purple-50 rounded-lg shadow-md overflow-hidden mb-6 border-2 border-transparent hover:border-pink-200 transition-all duration-300">
            <div class="bg-white bg-opacity-70 backdrop-blur-sm p-5">
                <!-- Filter Header -->
                <div class="flex items-center justify-between mb-5">
                    <h3 class="text-lg font-bold text-gray-900 flex items-center">
                        <span class="text-2xl mr-2">🔍</span>
                        Filter Campaigns
                    </h3>
                    <div v-if="activeFilterCount > 0"
                         class="flex items-center gap-2 px-3 py-1 bg-pink-600 text-white rounded-full text-xs font-semibold animate-pulse">
                        <span class="inline-block w-2 h-2 bg-white rounded-full"></span>
                        {{ activeFilterCount }} Active Filter{{ activeFilterCount !== 1 ? 's' : '' }}
                    </div>
                </div>

                <!-- Quick Filter Presets -->
                <div class="mb-4 flex flex-wrap gap-2">
                    <button @click="applyQuickFilter('top10')"
                            class="px-3 py-1.5 bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-900 rounded-lg text-xs font-bold hover:from-yellow-500 hover:to-yellow-600 transition-all shadow-sm hover:shadow-md transform hover:scale-105">
                        <span class="mr-1">🏆</span> Top 10%
                    </button>
                    <button @click="applyQuickFilter('needsAttention')"
                            class="px-3 py-1.5 bg-gradient-to-r from-yellow-500 to-red-600 text-white rounded-lg text-xs font-semibold hover:from-yellow-600 hover:to-red-700 transition-all shadow-sm hover:shadow-md transform hover:scale-105">
                        <span class="mr-1">⚠️</span> Needs Attention
                    </button>
                    <button @click="applyQuickFilter('highPerformers')"
                            class="px-3 py-1.5 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg text-xs font-semibold hover:from-green-700 hover:to-green-800 transition-all shadow-sm hover:shadow-md transform hover:scale-105">
                        <span class="mr-1">⭐</span> High Performers
                    </button>
                </div>

                <div class="grid grid-cols-1 gap-5">
                    <!-- Search Box -->
                    <div>
                        <label class="block text-sm font-bold text-gray-800 mb-2 flex items-center">
                            <span class="mr-2 text-lg">🔎</span>
                            Search Campaigns
                        </label>
                        <div class="relative">
                            <input type="text"
                                   v-model="searchQuery"
                                   placeholder="Search by organization or campaign name..."
                                   class="w-full rounded-lg border-2 border-gray-200 shadow-sm focus:border-pink-500 focus:ring-2 focus:ring-pink-200 text-sm pl-10 pr-10 py-2.5 transition-all">
                            <div class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                                <i class="fas fa-search"></i>
                            </div>
                            <button v-if="searchQuery"
                                    @click="searchQuery = ''"
                                    class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-pink-600 transition-colors">
                                <i class="fas fa-times-circle"></i>
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Score Range Sliders -->
                <div class="mt-5 grid grid-cols-1 md:grid-cols-2 gap-5">
                    <!-- Min Score Filter -->
                    <div>
                        <label class="block text-sm font-bold text-gray-800 mb-2 flex items-center justify-between">
                            <span class="flex items-center">
                                <span class="mr-2 text-lg">📊</span>
                                Min Score
                            </span>
                            <span class="text-base font-bold text-pink-600 bg-pink-100 px-3 py-1 rounded-full">{{ minScore }}</span>
                        </label>
                        <div class="flex items-center gap-3">
                            <span class="text-xs text-gray-500 font-medium">0</span>
                            <input type="range"
                                   v-model.number="minScore"
                                   min="0"
                                   max="100"
                                   class="flex-1 h-2 rounded-lg cursor-pointer range-slider">
                            <span class="text-xs text-gray-500 font-medium">100</span>
                        </div>
                    </div>

                    <!-- Max Score Filter -->
                    <div>
                        <label class="block text-sm font-bold text-gray-800 mb-2 flex items-center justify-between">
                            <span class="flex items-center">
                                <span class="mr-2 text-lg">📈</span>
                                Max Score
                            </span>
                            <span class="text-base font-bold text-purple-600 bg-purple-100 px-3 py-1 rounded-full">{{ maxScore }}</span>
                        </label>
                        <div class="flex items-center gap-3">
                            <span class="text-xs text-gray-500 font-medium">0</span>
                            <input type="range"
                                   v-model.number="maxScore"
                                   min="0"
                                   max="100"
                                   class="flex-1 h-2 rounded-lg cursor-pointer range-slider-purple">
                            <span class="text-xs text-gray-500 font-medium">100</span>
                        </div>
                    </div>
                </div>

                <!-- Active Filters Display -->
                <div v-if="activeFiltersList.length > 0" class="mt-5 pt-4 border-t border-gray-200">
                    <div class="flex flex-wrap items-center gap-2">
                        <span class="text-xs font-semibold text-gray-600 mr-2">ACTIVE:</span>
                        <span v-for="filter in activeFiltersList" :key="filter.key"
                              class="inline-flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-full text-xs font-semibold shadow-sm">
                            {{ filter.label }}
                            <button @click="removeFilter(filter.key)"
                                    class="ml-1 hover:bg-white hover:bg-opacity-20 rounded-full p-0.5 transition-colors">
                                <i class="fas fa-times text-xs"></i>
                            </button>
                        </span>
                    </div>
                </div>

                <!-- Filter Summary & Reset -->
                <div class="mt-5 pt-4 border-t border-gray-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                    <button @click="resetFilters()"
                            :disabled="activeFilterCount === 0"
                            :class="activeFilterCount === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-pink-700 hover:scale-105'"
                            class="inline-flex items-center px-4 py-2 bg-gradient-to-r from-pink-600 to-pink-600 text-white rounded-lg font-semibold transition-all shadow-md">
                        <i class="fas fa-undo mr-2"></i> Reset All Filters
                    </button>
                    <div class="flex items-center gap-2 text-sm font-semibold">
                        <span class="text-gray-600">Showing</span>
                        <span class="px-3 py-1 bg-gradient-to-r from-pink-600 to-purple-600 text-white rounded-full">
                            {{ filteredCampaigns.length }}
                        </span>
                        <span class="text-gray-600">of</span>
                        <span class="px-3 py-1 bg-gray-200 text-gray-700 rounded-full">
                            {{ campaigns.length }}
                        </span>
                        <span class="text-gray-600">campaign{{ filteredCampaigns.length !== 1 ? 's' : '' }}</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Data Visualizations Dashboard -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <!-- Score Distribution Chart -->
            <div class="bg-white rounded-lg shadow-sm border border-pink-100 p-6">
                <h3 class="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <i class="fas fa-chart-bar text-pink-500 mr-2"></i>
                    Score Distribution
                </h3>
                <canvas ref="scoreDistributionChart" class="w-full" style="max-height: 250px;"></canvas>
            </div>

            <!-- Top 10 Campaigns Chart -->
            <div class="bg-white rounded-lg shadow-sm border border-pink-100 p-6">
                <h3 class="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <i class="fas fa-trophy text-pink-500 mr-2"></i>
                    Top 10 Campaigns
                </h3>
                <canvas ref="top10Chart" class="w-full" style="max-height: 300px;"></canvas>
            </div>
        </div>

        <!-- Performance Analytics Dashboard -->
        <div class="mb-6 bg-gradient-to-r from-pink-50 to-purple-50 rounded-lg shadow-md overflow-hidden">
            <div class="px-6 py-4 bg-white bg-opacity-60 backdrop-blur-sm">
                <h3 class="text-lg font-semibold text-gray-900 mb-4">Campaign Performance Analytics</h3>

                <div class="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                    <!-- Average Score -->
                    <div class="bg-white rounded-lg p-3 shadow-sm">
                        <p class="text-xs text-gray-500">Avg Score</p>
                        <p class="text-xl font-bold text-gray-900">{{ cohortStats.avgScore.toFixed(1) }}</p>
                    </div>
                    <!-- Highest Score -->
                    <div class="bg-white rounded-lg p-3 shadow-sm">
                        <p class="text-xs text-gray-500">Highest Score</p>
                        <p class="text-xl font-bold text-green-600">{{ cohortStats.maxScore.toFixed(1) }}</p>
                    </div>
                    <!-- Avg Donors -->
                    <div class="bg-white rounded-lg p-3 shadow-sm">
                        <p class="text-xs text-gray-500">Avg Donors</p>
                        <p class="text-xl font-bold text-pink-600">{{ Math.round(cohortStats.avgDonors) }}</p>
                    </div>
                    <!-- Total Raised -->
                    <div class="bg-white rounded-lg p-3 shadow-sm">
                        <p class="text-xs text-gray-500">Total Raised</p>
                        <p class="text-xl font-bold text-green-600">${{ formatCurrency(cohortStats.totalRaised) }}</p>
                    </div>
                </div>

                <!-- How Scoring Works -->
                <div class="bg-white rounded-lg p-4 shadow-sm mt-4">
                    <h4 class="text-sm font-semibold text-gray-700 mb-3">How Scoring Works</h4>
                    <div class="text-sm text-gray-600">
                        <p class="mb-2">Campaigns are ranked based on their impact score (0-100), which combines:</p>
                        <ul class="list-none space-y-1">
                            <li class="flex items-center">
                                <span class="inline-flex items-center justify-center w-12 h-6 rounded bg-pink-100 text-pink-700 font-semibold text-xs mr-2">40%</span>
                                <span><strong>Donor Count</strong> - Unique donors</span>
                            </li>
                            <li class="flex items-center">
                                <span class="inline-flex items-center justify-center w-12 h-6 rounded bg-pink-100 text-pink-700 font-semibold text-xs mr-2">25%</span>
                                <span><strong>Avg $/Donor</strong> - Average donation</span>
                            </li>
                            <li class="flex items-center">
                                <span class="inline-flex items-center justify-center w-12 h-6 rounded bg-pink-100 text-pink-700 font-semibold text-xs mr-2">15%</span>
                                <span><strong>Reach</strong> - Unique views</span>
                            </li>
                            <li class="flex items-center">
                                <span class="inline-flex items-center justify-center w-12 h-6 rounded bg-pink-100 text-pink-700 font-semibold text-xs mr-2">10%</span>
                                <span><strong>Fundraising</strong> - Total raised</span>
                            </li>
                            <li class="flex items-center">
                                <span class="inline-flex items-center justify-center w-12 h-6 rounded bg-pink-100 text-pink-700 font-semibold text-xs mr-2">10%</span>
                                <span><strong>Conversion</strong> - Donations/views</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>

        <!-- Empty State for Filtered Results -->
        <div v-if="filteredCampaigns.length === 0" class="text-center py-12">
            <div class="text-6xl mb-4">🔍</div>
            <h3 class="text-xl font-semibold text-gray-900 mb-2">No Campaigns Match Your Filters</h3>
            <p class="text-gray-600 mb-4">Try adjusting your filters or search criteria</p>
            <button @click="resetFilters()"
                    class="bg-pink-600 text-white px-6 py-2 rounded-lg hover:bg-pink-700 font-medium transition-all">
                Clear All Filters
            </button>
        </div>

        <!-- Sort Tabs (Table Header Style) -->
        <div v-if="filteredCampaigns.length > 0" class="bg-white rounded-t-lg shadow-sm border-t border-l border-r border-pink-100 overflow-hidden">
            <div class="flex border-b-2 border-pink-200">
                <button @click="sortBy = 'rank'"
                        :class="sortBy === 'rank' ? 'bg-gradient-to-r from-pink-500 to-pink-600 text-white' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'"
                        :aria-current="sortBy === 'rank' ? 'true' : null"
                        class="flex-1 px-4 sm:px-6 py-3 sm:py-3 min-h-[48px] text-xs sm:text-sm font-semibold uppercase tracking-wider border-r border-gray-200 transition-colors">
                    Overall
                </button>

                <button @click="sortBy = 'donors-desc'"
                        :class="sortBy === 'donors-desc' ? 'bg-gradient-to-r from-pink-500 to-pink-600 text-white' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'"
                        :aria-current="sortBy === 'donors-desc' ? 'true' : null"
                        class="flex-1 px-4 sm:px-6 py-3 sm:py-3 min-h-[48px] text-xs sm:text-sm font-semibold uppercase tracking-wider border-r border-gray-200 transition-colors">
                    Donors
                </button>

                <button @click="sortBy = 'avgPerDonor-desc'"
                        :class="sortBy === 'avgPerDonor-desc' ? 'bg-gradient-to-r from-pink-500 to-pink-600 text-white' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'"
                        :aria-current="sortBy === 'avgPerDonor-desc' ? 'true' : null"
                        class="flex-1 px-4 sm:px-6 py-3 sm:py-3 min-h-[48px] text-xs sm:text-sm font-semibold uppercase tracking-wider border-r border-gray-200 transition-colors">
                    Avg $/Donor
                </button>

                <button @click="sortBy = 'raised-desc'"
                        :class="sortBy === 'raised-desc' ? 'bg-gradient-to-r from-pink-500 to-pink-600 text-white' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'"
                        :aria-current="sortBy === 'raised-desc' ? 'true' : null"
                        class="flex-1 px-4 sm:px-6 py-3 sm:py-3 min-h-[48px] text-xs sm:text-sm font-semibold uppercase tracking-wider border-r border-gray-200 transition-colors">
                    Raised
                </button>

                <button @click="sortBy = 'views-desc'"
                        :class="sortBy === 'views-desc' ? 'bg-gradient-to-r from-pink-500 to-pink-600 text-white' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'"
                        :aria-current="sortBy === 'views-desc' ? 'true' : null"
                        class="flex-1 px-4 sm:px-6 py-3 sm:py-3 min-h-[48px] text-xs sm:text-sm font-semibold uppercase tracking-wider border-r border-gray-200 transition-colors">
                    Views
                </button>

                <button @click="sortBy = 'conversion-desc'"
                        :class="sortBy === 'conversion-desc' ? 'bg-gradient-to-r from-pink-500 to-pink-600 text-white' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'"
                        :aria-current="sortBy === 'conversion-desc' ? 'true' : null"
                        class="flex-1 px-4 sm:px-6 py-3 sm:py-3 min-h-[48px] text-xs sm:text-sm font-semibold uppercase tracking-wider transition-colors">
                    Conversion
                </button>
            </div>
        </div>

        <!-- Mobile Card Layout (< 640px) -->
        <div v-if="filteredCampaigns.length > 0" class="sm:hidden space-y-4 rounded-b-lg border-b border-l border-r border-pink-100 p-4 bg-white shadow-sm">
            <div v-for="(campaign, index) in filteredCampaigns" :key="campaign.id"
                 class="bg-white rounded-lg shadow-md border-2 border-pink-100 overflow-hidden transform transition-all duration-200 hover:scale-[1.02] hover:shadow-xl">
                <!-- Rank Badge -->
                <div class="bg-gradient-to-r from-pink-500 to-pink-600 text-white px-4 py-2 flex justify-between items-center">
                    <span class="font-bold text-lg">#{{ campaign.rank }}</span>
                    <span v-if="campaign.rank <= 3" class="text-2xl">{{ ['🥇', '🥈', '🥉'][campaign.rank - 1] }}</span>
                </div>

                <!-- Card Content -->
                <div class="p-4">
                    <h3 class="text-lg font-bold mb-2 text-gray-900">{{ getDisplayName(campaign) }}</h3>

                    <!-- Impact Score -->
                    <div class="flex items-center justify-between mb-4">
                        <div>
                            <div :class="{'active-cell': highlightedColumns.includes('impact_score'), 'muted-cell': !highlightedColumns.includes('impact_score')}" class="text-4xl font-bold">{{ campaign.impact_score ? campaign.impact_score.toFixed(1) : 'N/A' }}</div>
                            <div class="text-sm text-gray-600">Impact Score</div>
                        </div>
                    </div>

                    <!-- Stats Grid -->
                    <div class="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                        <div class="text-center">
                            <div class="text-xs text-gray-600">Donors</div>
                            <div :class="{'active-cell': highlightedColumns.includes('donors'), 'muted-cell': !highlightedColumns.includes('donors')}" class="text-lg font-semibold">{{ formatNumber(campaign.impact_donor_count || 0) }}</div>
                        </div>
                        <div class="text-center">
                            <div class="text-xs text-gray-600">Avg $/Donor</div>
                            <div :class="{'active-cell': highlightedColumns.includes('avgPerDonor'), 'muted-cell': !highlightedColumns.includes('avgPerDonor')}" class="text-lg font-semibold flex items-center justify-center gap-1">
                                <span>${{ formatCurrency(campaign.impact_avg_per_donor || 0) }}</span>
                                <span>{{ getAvgPerDonorTier(campaign.impact_avg_per_donor).emoji }}</span>
                            </div>
                        </div>
                        <div class="text-center">
                            <div class="text-xs text-gray-600">Raised</div>
                            <div :class="{'active-cell': highlightedColumns.includes('raised'), 'muted-cell': !highlightedColumns.includes('raised')}" class="text-lg font-semibold">${{ formatCurrency(campaign.total_raised_with_children_campaigns || 0) }}</div>
                        </div>
                        <div class="text-center">
                            <div class="text-xs text-gray-600">Views</div>
                            <div :class="{'active-cell': highlightedColumns.includes('views'), 'muted-cell': !highlightedColumns.includes('views')}" class="text-lg font-semibold">{{ formatNumber(campaign.impact_unique_views || 0) }}</div>
                        </div>
                        <div class="text-center">
                            <div class="text-xs text-gray-600">Conversion</div>
                            <div v-if="isValidConversion(campaign)" :class="{'active-cell': highlightedColumns.includes('conversion'), 'muted-cell': !highlightedColumns.includes('conversion')}" class="text-lg font-semibold">
                                {{ campaign.impact_conversion_rate.toFixed(2) }}%
                            </div>
                            <div v-else class="text-sm text-gray-400" title="Requires 10+ views">N/A</div>
                        </div>
                    </div>

                    <!-- Action Button -->
                    <a :href="`/admin/reports/impact-leaderboard/campaign/${campaign.id}`"
                       class="block w-full bg-gradient-to-r from-pink-500 to-pink-600 text-white text-center py-3 rounded-lg font-semibold hover:from-pink-600 hover:to-pink-700 transition-all">
                        View Details
                    </a>
                </div>
            </div>
        </div>

        <!-- Desktop Table (≥ 640px) -->
        <div v-if="filteredCampaigns.length > 0"
             class="hidden sm:block bg-white rounded-b-lg shadow-sm border-b border-l border-r border-pink-100 overflow-hidden">
            <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gradient-to-r from-pink-500 to-pink-600">
                    <tr>
                        <th class="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                            Rank
                        </th>
                        <th class="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Campaign Name</th>
                        <th class="px-6 py-3 text-center text-xs font-medium text-white uppercase tracking-wider">
                            Impact Score
                        </th>
                        <th class="px-6 py-3 text-center text-xs font-medium text-white uppercase tracking-wider">
                            Donors
                        </th>
                        <th class="px-6 py-3 text-center text-xs font-medium text-white uppercase tracking-wider">
                            Avg $/Donor
                        </th>
                        <th class="px-6 py-3 text-center text-xs font-medium text-white uppercase tracking-wider">
                            Total Raised
                        </th>
                        <th class="px-6 py-3 text-center text-xs font-medium text-white uppercase tracking-wider">
                            Views
                        </th>
                        <th class="px-6 py-3 text-center text-xs font-medium text-white uppercase tracking-wider">
                            Conversion
                        </th>
                        <th class="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Action</th>
                    </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                    <tr v-for="campaign in filteredCampaigns" :key="campaign.id"
                        class="hover:bg-pink-50 transition-colors cursor-pointer"
                        :class="{'bg-yellow-50': campaign.rank <= 3}">
                        <!-- Rank with Trophy -->
                        <td class="px-6 py-4 whitespace-nowrap">
                            <div class="flex items-center">
                                <span class="font-bold text-gray-900 mr-2">#{{ campaign.rank }}</span>
                                <span v-if="campaign.rank <= 3">{{ ['🥇', '🥈', '🥉'][campaign.rank - 1] }}</span>
                            </div>
                        </td>

                        <!-- Campaign Name -->
                        <td class="px-6 py-4">
                            <div class="text-sm font-medium text-gray-900">{{ getDisplayName(campaign) }}</div>
                        </td>

                        <!-- Impact Score -->
                        <td :class="{'active-cell': highlightedColumns.includes('impact_score'), 'muted-cell': !highlightedColumns.includes('impact_score')}" class="px-6 py-4 whitespace-nowrap text-center">
                            <div class="text-lg font-bold">{{ campaign.impact_score ? campaign.impact_score.toFixed(1) : 'N/A' }}</div>
                        </td>

                        <!-- Donors -->
                        <td :class="{'active-cell': highlightedColumns.includes('donors'), 'muted-cell': !highlightedColumns.includes('donors')}" class="px-6 py-4 whitespace-nowrap text-center text-sm font-semibold">{{ formatNumber(campaign.impact_donor_count || 0) }}</td>

                        <!-- Avg Per Donor -->
                        <td :class="{'active-cell': highlightedColumns.includes('avgPerDonor'), 'muted-cell': !highlightedColumns.includes('avgPerDonor')}" class="px-6 py-4 whitespace-nowrap text-center text-sm">
                            <div class="flex items-center justify-center gap-1">
                                <span class="font-semibold">${{ formatCurrency(campaign.impact_avg_per_donor || 0) }}</span>
                                <span :title="getAvgPerDonorTier(campaign.impact_avg_per_donor).label">{{ getAvgPerDonorTier(campaign.impact_avg_per_donor).emoji }}</span>
                            </div>
                        </td>

                        <!-- Total Raised -->
                        <td :class="{'active-cell': highlightedColumns.includes('raised'), 'muted-cell': !highlightedColumns.includes('raised')}" class="px-6 py-4 whitespace-nowrap text-center text-sm font-semibold">${{ formatCurrency(campaign.total_raised_with_children_campaigns || 0) }}</td>

                        <!-- Views -->
                        <td :class="{'active-cell': highlightedColumns.includes('views'), 'muted-cell': !highlightedColumns.includes('views')}" class="px-6 py-4 whitespace-nowrap text-center text-sm">{{ formatNumber(campaign.impact_unique_views || 0) }}</td>

                        <!-- Conversion Rate -->
                        <td :class="{'active-cell': highlightedColumns.includes('conversion'), 'muted-cell': !highlightedColumns.includes('conversion')}" class="px-6 py-4 whitespace-nowrap text-center text-sm font-semibold">
                            <span v-if="isValidConversion(campaign)">
                                {{ campaign.impact_conversion_rate.toFixed(2) }}%
                            </span>
                            <span v-else class="text-gray-400 text-xs" title="Requires 10+ views for valid conversion rate">
                                N/A
                            </span>
                        </td>

                        <!-- Action -->
                        <td class="px-6 py-4 whitespace-nowrap text-sm">
                            <a :href="`/admin/reports/impact-leaderboard/campaign/${campaign.id}`"
                               class="text-pink-600 hover:text-pink-900 font-medium">
                                View Details →
                            </a>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
import { Chart } from 'chart.js/auto'

export default {
    name: 'CampaignLeaderboard',

    props: {
        foundation: {
            type: Object,
            required: true
        },
        campaigns: {
            type: Array,
            required: true
        }
    },

    data() {
        return {
            searchQuery: '',
            minScore: 0,
            maxScore: 100,
            sortBy: 'rank',
            scoreChart: null,
            top10Chart: null
        }
    },

    computed: {
        enrichedCampaigns() {
            return this.campaigns.map((c, idx) => {
                return { ...c, rank: c.rank || (idx + 1) }
            })
        },

        filteredCampaigns() {
            let filtered = this.enrichedCampaigns

            // Search filter - search both campaign name and organization name (business_name)
            if (this.searchQuery) {
                const query = this.searchQuery.toLowerCase()
                filtered = filtered.filter(c =>
                    c.name.toLowerCase().includes(query) ||
                    (c.business_name && c.business_name.toLowerCase().includes(query))
                )
            }

            // Score filter
            filtered = filtered.filter(c =>
                (c.impact_score || 0) >= this.minScore && (c.impact_score || 0) <= this.maxScore
            )

            // Sort
            filtered = this.sortCampaigns(filtered)

            // Re-rank after filtering
            return filtered.map((c, idx) => ({ ...c, rank: idx + 1 }))
        },

        cohortStats() {
            const campaigns = this.enrichedCampaigns.filter(c => c.impact_score)
            const count = campaigns.length

            if (count === 0) {
                return {
                    avgScore: 0,
                    maxScore: 0,
                    minScore: 0,
                    avgDonors: 0,
                    totalRaised: 0
                }
            }

            return {
                avgScore: campaigns.reduce((sum, c) => sum + (c.impact_score || 0), 0) / count,
                maxScore: Math.max(...campaigns.map(c => c.impact_score || 0)),
                minScore: Math.min(...campaigns.map(c => c.impact_score || 0)),
                avgDonors: campaigns.reduce((sum, c) => sum + (c.impact_donor_count || 0), 0) / count,
                totalRaised: campaigns.reduce((sum, c) => sum + (c.total_raised_with_children_campaigns || 0), 0)
            }
        },

        activeFilterCount() {
            let count = 0
            if (this.searchQuery) count++
            if (this.minScore > 0) count++
            if (this.maxScore < 100) count++
            return count
        },

        activeFiltersList() {
            const filters = []
            if (this.searchQuery) {
                filters.push({ key: 'search', label: `Search: "${this.searchQuery}"` })
            }
            if (this.minScore > 0) {
                filters.push({ key: 'minScore', label: `Min Score: ${this.minScore}` })
            }
            if (this.maxScore < 100) {
                filters.push({ key: 'maxScore', label: `Max Score: ${this.maxScore}` })
            }
            return filters
        },

        highlightedColumns() {
            // Returns array of column identifiers to highlight based on active sort
            switch(this.sortBy) {
                case 'rank':
                    return ['impact_score']  // Only highlight impact score, not rank
                case 'donors-desc':
                    return ['donors']
                case 'avgPerDonor-desc':
                    return ['avgPerDonor']
                case 'raised-desc':
                    return ['raised']
                case 'conversion-desc':
                    return ['conversion']
                case 'views-desc':
                    return ['views']
                default:
                    return []
            }
        }
    },

    mounted() {
        this.initCharts()
        this.loadFiltersFromUrl()
    },

    beforeUnmount() {
        // Destroy charts to prevent memory leaks
        if (this.scoreChart) this.scoreChart.destroy()
        if (this.top10Chart) this.top10Chart.destroy()
    },

    watch: {
        searchQuery() { this.updateUrl() },
        minScore() { this.updateUrl() },
        maxScore() { this.updateUrl() }
    },

    methods: {
        formatNumber(num) {
            return (num || 0).toLocaleString()
        },

        formatCurrency(amount) {
            // Value is already in dollars (stored as integer), format with commas
            return (amount || 0).toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            })
        },

        getAvgPerDonorTier(avgPerDonor) {
            if (!avgPerDonor || avgPerDonor <= 0) return { emoji: '—', label: 'N/A', color: 'gray' }
            if (avgPerDonor >= 250) return { emoji: '🏆', label: 'Elite', color: 'yellow' }
            if (avgPerDonor >= 100) return { emoji: '💎', label: 'Excellent', color: 'blue' }
            if (avgPerDonor >= 50) return { emoji: '✅', label: 'Good', color: 'green' }
            if (avgPerDonor >= 25) return { emoji: '⚠️', label: 'Average', color: 'amber' }
            return { emoji: '❌', label: 'Below Avg', color: 'red' }
        },

        getDisplayName(campaign) {
            // Use organization name (business_name) if available, otherwise fall back to campaign name
            return campaign.business_name || campaign.name
        },

        isValidConversion(campaign) {
            // Require at least 10 views for valid conversion rate
            return (campaign.impact_unique_views || 0) >= 10
        },

        getDisplayConversion(campaign) {
            if (!this.isValidConversion(campaign)) {
                return 'N/A'
            }
            return campaign.impact_conversion_rate ? campaign.impact_conversion_rate.toFixed(2) + '%' : 'N/A'
        },

        resetFilters() {
            this.searchQuery = ''
            this.minScore = 0
            this.maxScore = 100
            this.sortBy = 'rank'
        },

        removeFilter(key) {
            switch(key) {
                case 'search':
                    this.searchQuery = ''
                    break
                case 'minScore':
                    this.minScore = 0
                    break
                case 'maxScore':
                    this.maxScore = 100
                    break
            }
        },

        applyQuickFilter(preset) {
            this.resetFilters()

            switch(preset) {
                case 'top10':
                    // Top 10% - scores 90+
                    this.minScore = 90
                    break
                case 'needsAttention':
                    // Needs Attention - scores below 60
                    this.maxScore = 59
                    break
                case 'highPerformers':
                    // High Performers - scores 80+
                    this.minScore = 80
                    break
            }
        },

        updateUrl() {
            const params = new URLSearchParams()

            if (this.searchQuery) params.set('search', this.searchQuery)
            if (this.minScore > 0) params.set('min_score', this.minScore)
            if (this.maxScore < 100) params.set('max_score', this.maxScore)

            const queryString = params.toString()
            const newUrl = queryString ? `${window.location.pathname}?${queryString}` : window.location.pathname

            // Update URL without page reload
            window.history.replaceState({}, '', newUrl)
        },

        loadFiltersFromUrl() {
            const params = new URLSearchParams(window.location.search)

            if (params.has('search')) this.searchQuery = params.get('search')
            if (params.has('min_score')) this.minScore = parseInt(params.get('min_score'))
            if (params.has('max_score')) this.maxScore = parseInt(params.get('max_score'))
        },

        sortCampaigns(campaigns) {
            const sorted = [...campaigns]

            switch(this.sortBy) {
                case 'rank':
                    return sorted.sort((a, b) => a.rank - b.rank)
                case 'donors-desc':
                    return sorted.sort((a, b) => (b.impact_donor_count || 0) - (a.impact_donor_count || 0))
                case 'avgPerDonor-desc':
                    return sorted.sort((a, b) => (b.impact_avg_per_donor || 0) - (a.impact_avg_per_donor || 0))
                case 'raised-desc':
                    return sorted.sort((a, b) => (b.total_raised_with_children_campaigns || 0) - (a.total_raised_with_children_campaigns || 0))
                case 'conversion-desc':
                    return sorted.sort((a, b) => {
                        const aRate = this.isValidConversion(a) ? (a.impact_conversion_rate || 0) : 0
                        const bRate = this.isValidConversion(b) ? (b.impact_conversion_rate || 0) : 0
                        return bRate - aRate
                    })
                case 'views-desc':
                    return sorted.sort((a, b) => (b.impact_unique_views || 0) - (a.impact_unique_views || 0))
                default:
                    return sorted
            }
        },

        initCharts() {
            // Score Distribution Chart
            const scoreCtx = this.$refs.scoreDistributionChart
            if (scoreCtx) {
                const scoreRanges = [0, 0, 0, 0, 0]
                this.enrichedCampaigns.forEach(c => {
                    if (c.impact_score) {
                        const rangeIndex = Math.min(Math.floor(c.impact_score / 20), 4)
                        scoreRanges[rangeIndex]++
                    }
                })

                this.scoreChart = new Chart(scoreCtx, {
                    type: 'bar',
                    data: {
                        labels: ['0-20', '21-40', '41-60', '61-80', '81-100'],
                        datasets: [{
                            label: 'Number of Campaigns',
                            data: scoreRanges,
                            backgroundColor: [
                                'rgba(244, 67, 54, 0.8)',
                                'rgba(255, 152, 0, 0.8)',
                                'rgba(255, 235, 59, 0.8)',
                                'rgba(33, 150, 243, 0.8)',
                                'rgba(76, 175, 80, 0.8)',
                            ],
                            borderColor: [
                                'rgb(244, 67, 54)',
                                'rgb(255, 152, 0)',
                                'rgb(255, 235, 59)',
                                'rgb(33, 150, 243)',
                                'rgb(76, 175, 80)',
                            ],
                            borderWidth: 1
                        }]
                    },
                    options: {
                        responsive: true,
                        maintainAspectRatio: true,
                        plugins: {
                            legend: {
                                display: false
                            }
                        },
                        scales: {
                            y: {
                                beginAtZero: true,
                                ticks: {
                                    stepSize: 1
                                }
                            }
                        }
                    }
                })
            }

            // Top 10 Campaigns Chart
            const top10Ctx = this.$refs.top10Chart
            if (top10Ctx) {
                const sortedByScore = [...this.enrichedCampaigns]
                    .filter(c => c.impact_score)
                    .sort((a, b) => b.impact_score - a.impact_score)
                    .slice(0, 10)

                this.top10Chart = new Chart(top10Ctx, {
                    type: 'bar',
                    data: {
                        labels: sortedByScore.map(c => {
                            const displayName = this.getDisplayName(c)
                            return displayName.length > 30 ? displayName.substring(0, 27) + '...' : displayName
                        }),
                        datasets: [{
                            label: 'Impact Score',
                            data: sortedByScore.map(c => c.impact_score),
                            backgroundColor: sortedByScore.map((c, i) => {
                                if (i === 0) return 'rgba(255, 193, 7, 0.8)' // Gold
                                if (i === 1) return 'rgba(158, 158, 158, 0.8)' // Silver
                                if (i === 2) return 'rgba(205, 127, 50, 0.8)' // Bronze
                                return 'rgba(233, 30, 99, 0.8)' // Pink
                            }),
                            borderColor: sortedByScore.map((c, i) => {
                                if (i === 0) return 'rgb(255, 193, 7)'
                                if (i === 1) return 'rgb(158, 158, 158)'
                                if (i === 2) return 'rgb(205, 127, 50)'
                                return 'rgb(233, 30, 99)'
                            }),
                            borderWidth: 2
                        }]
                    },
                    options: {
                        indexAxis: 'y',
                        responsive: true,
                        maintainAspectRatio: true,
                        plugins: {
                            legend: {
                                display: false
                            }
                        },
                        scales: {
                            x: {
                                beginAtZero: true,
                                max: 100
                            }
                        }
                    }
                })
            }
        }
    }
}
</script>

<style scoped>
/* Pink Range Slider Styling */
.range-slider {
    -webkit-appearance: none;
    appearance: none;
    background: linear-gradient(to right, #fce7f3, #db2777); /* Pink gradient background */
    outline: none;
    height: 8px;
    border-radius: 4px;
}

/* Webkit browsers (Chrome, Safari) - Thumb */
.range-slider::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 22px;
    height: 22px;
    background: linear-gradient(135deg, #ec4899, #db2777); /* Pink gradient thumb */
    cursor: pointer;
    border-radius: 50%;
    border: 3px solid white;
    box-shadow: 0 2px 6px rgba(219, 39, 119, 0.4);
    transition: all 0.2s ease;
}

/* Firefox - Thumb */
.range-slider::-moz-range-thumb {
    width: 22px;
    height: 22px;
    background: linear-gradient(135deg, #ec4899, #db2777); /* Pink gradient thumb */
    cursor: pointer;
    border-radius: 50%;
    border: 3px solid white;
    box-shadow: 0 2px 6px rgba(219, 39, 119, 0.4);
    transition: all 0.2s ease;
}

/* Firefox - Track */
.range-slider::-moz-range-track {
    background: linear-gradient(to right, #fce7f3, #db2777); /* Pink gradient */
    border-radius: 4px;
    height: 8px;
}

/* Hover effect */
.range-slider:hover::-webkit-slider-thumb {
    background: linear-gradient(135deg, #db2777, #be185d); /* Darker pink on hover */
    transform: scale(1.1);
    box-shadow: 0 3px 8px rgba(219, 39, 119, 0.6);
}

.range-slider:hover::-moz-range-thumb {
    background: linear-gradient(135deg, #db2777, #be185d); /* Darker pink on hover */
    transform: scale(1.1);
    box-shadow: 0 3px 8px rgba(219, 39, 119, 0.6);
}

/* Purple Range Slider Styling */
.range-slider-purple {
    -webkit-appearance: none;
    appearance: none;
    background: linear-gradient(to right, #f3e8ff, #9333ea); /* Purple gradient background */
    outline: none;
    height: 8px;
    border-radius: 4px;
}

/* Webkit browsers (Chrome, Safari) - Thumb */
.range-slider-purple::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 22px;
    height: 22px;
    background: linear-gradient(135deg, #a855f7, #9333ea); /* Purple gradient thumb */
    cursor: pointer;
    border-radius: 50%;
    border: 3px solid white;
    box-shadow: 0 2px 6px rgba(147, 51, 234, 0.4);
    transition: all 0.2s ease;
}

/* Firefox - Thumb */
.range-slider-purple::-moz-range-thumb {
    width: 22px;
    height: 22px;
    background: linear-gradient(135deg, #a855f7, #9333ea); /* Purple gradient thumb */
    cursor: pointer;
    border-radius: 50%;
    border: 3px solid white;
    box-shadow: 0 2px 6px rgba(147, 51, 234, 0.4);
    transition: all 0.2s ease;
}

/* Firefox - Track */
.range-slider-purple::-moz-range-track {
    background: linear-gradient(to right, #f3e8ff, #9333ea); /* Purple gradient */
    border-radius: 4px;
    height: 8px;
}

/* Hover effect */
.range-slider-purple:hover::-webkit-slider-thumb {
    background: linear-gradient(135deg, #9333ea, #7e22ce); /* Darker purple on hover */
    transform: scale(1.1);
    box-shadow: 0 3px 8px rgba(147, 51, 234, 0.6);
}

.range-slider-purple:hover::-moz-range-thumb {
    background: linear-gradient(135deg, #9333ea, #7e22ce); /* Darker purple on hover */
    transform: scale(1.1);
    box-shadow: 0 3px 8px rgba(147, 51, 234, 0.6);
}

/* Animation for filter count badge */
@keyframes pulse {
    0%, 100% {
        opacity: 1;
    }
    50% {
        opacity: 0.7;
    }
}

/* Active column data - pink, larger, bolder (NO background) */
.active-cell {
    font-weight: 700 !important;
    font-size: 1.15em !important;
    color: #db2777 !important; /* pink-600 */
    transition: all 0.2s ease;
}

/* Muted column data - gray, reduced opacity (NO background) */
.muted-cell {
    color: #9ca3af !important; /* gray-400 */
    opacity: 0.65;
    font-weight: 400;
    transition: all 0.2s ease;
}

.muted-cell:hover {
    opacity: 0.85;
}
</style>
